import React, { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import LoadingSpinner from '../components/LoadingSpinner';
import Table from '../components/Table';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import { Send, RefreshCw, Filter, Phone, Save, X, Check, MessageCircle, Calendar, FileText, Search, AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';

export default function CobrancaWhatsApp() {
  const { 
    getClientesEmAtraso, 
    enviarCobrancaWhatsApp, 
    getStatusWhatsApp, 
    salvarDadosCobranca,
    whatsappStatus
  } = useApi();
  
  const [clientes, setClientes] = useState([]);
  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [editando, setEditando] = useState({});
  
  // Modal de envio WhatsApp
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [telefoneModal, setTelefoneModal] = useState('');
  const [mensagemModal, setMensagemModal] = useState('');
  
  // Modal de visualização de parcelas
  const [showParcelasModal, setShowParcelasModal] = useState(false);
  const [clienteParcelas, setClienteParcelas] = useState(null);
  
  // Modal de data para cobrança presencial
  const [showDataModal, setShowDataModal] = useState(false);
  const [clienteDataPresencial, setClienteDataPresencial] = useState(null);
  const [dataAgendamentoModal, setDataAgendamentoModal] = useState('');
  
  // Modal de lembrete
  const [showLembreteModal, setShowLembreteModal] = useState(false);
  const [clienteLembrete, setClienteLembrete] = useState(null);
  const [dataLembreteModal, setDataLembreteModal] = useState('');
  const [mensagemLembreteModal, setMensagemLembreteModal] = useState('');
  
  // Resultado do envio
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultado, setResultado] = useState(null);
  
  // Busca por nome ou código
  const [buscaCliente, setBuscaCliente] = useState('');
  
  // Filtros de dias de atraso (padrão 30-90)
  const [diasMin, setDiasMin] = useState(30);
  const [diasMax, setDiasMax] = useState(1440);

  // Modal SPC
  const [showSpcModal, setShowSpcModal] = useState(false);
  const [clientesSpc, setClientesSpc] = useState([]);
  const [loadingSpc, setLoadingSpc] = useState(false);

  // Modal Cobranças por Zap
  const [showZapModal, setShowZapModal] = useState(false);
  const [cobrancasZap, setCobrancasZap] = useState([]);
  const [loadingZap, setLoadingZap] = useState(false);
  const hoje = new Date().toISOString().split('T')[0];
  const trintaDiasAtras = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [dataInicioZap, setDataInicioZap] = useState(trintaDiasAtras);
  const [dataFimZap, setDataFimZap] = useState(hoje);

  // Ordenação por dias de atraso
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' = menor atraso primeiro

  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 20;

  // Contador de alterações não salvas
  const totalAlteracoesNaoSalvas = Object.keys(editando).length;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    aplicarFiltro();
  }, [diasMin, diasMax, clientes, buscaCliente, sortOrder]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Atualizar status do WhatsApp
      await getStatusWhatsApp();
      
      // Buscar clientes
      const clientesData = await getClientesEmAtraso(30);
      
      // Adicionar campos de controle aos clientes
      const clientesComControle = (clientesData.clientes || []).map(c => ({
        ...c,
        cobranca_zap: c.cobranca_zap || false,
        cobranca_presencial: c.cobranca_presencial || false,
        resposta: c.resposta || '',
        data_agendamento: c.data_agendamento || '',
        acao: c.acao || '',
        spc: c.spc || false,
        data_ultima_cobranca_zap: c.data_ultima_cobranca_zap || null,
        aviso_reenvio_zap: c.aviso_reenvio_zap || false
      }));
      
      setClientes(clientesComControle);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      alert('Erro ao carregar clientes em atraso');
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltro = () => {
    let filtrados = clientes.filter(
      c => c.dias_atraso >= diasMin && c.dias_atraso <= diasMax
    );

    // Filtrar por nome ou código se houver busca
    if (buscaCliente.trim()) {
      const termoBusca = buscaCliente.trim().toLowerCase();
      filtrados = filtrados.filter(c => {
        const nome = (c.nome_cliente || '').toLowerCase();
        const codigo = (c.codigo_cliente || '').toString();
        return nome.includes(termoBusca) || codigo.includes(termoBusca);
      });
    }

    filtrados.sort((a, b) =>
      sortOrder === 'asc'
        ? a.dias_atraso - b.dias_atraso
        : b.dias_atraso - a.dias_atraso
    );

    setClientesFiltrados(filtrados);
    setPaginaAtual(1);
  };

  const handleConsultarSpc = async () => {
    try {
      setLoadingSpc(true);
      setShowSpcModal(true);
      const response = await fetch('/api/cobranca/clientes-spc');
      const data = await response.json();
      setClientesSpc(data.data || []);
    } catch (error) {
      console.error('Erro ao buscar clientes SPC:', error);
      alert('Erro ao buscar clientes no SPC');
    } finally {
      setLoadingSpc(false);
    }
  };

  const handleConsultarCobrancasZap = async (inicio, fim) => {
    try {
      setLoadingZap(true);
      const response = await fetch(`/api/cobranca/historico-zap-periodo?dataInicio=${inicio}&dataFim=${fim}`);
      const data = await response.json();
      setCobrancasZap(data.data || []);
    } catch (error) {
      console.error('Erro ao buscar cobranças por zap:', error);
      alert('Erro ao buscar cobranças por WhatsApp');
    } finally {
      setLoadingZap(false);
    }
  };

  const handleRemoverSpc = async (ordemCliente) => {
    if (!confirm('Remover marcação SPC deste cliente?')) return;
    try {
      const cliente = clientes.find(c => c.ordem_cliente === ordemCliente);
      const dadosAtuais = cliente || {};
      await salvarDadosCobranca({
        ...dadosAtuais,
        ordem_cliente: ordemCliente,
        spc: false
      });
      // Atualizar lista SPC e lista principal
      setClientesSpc(prev => prev.filter(c => c.ordem_cliente !== ordemCliente));
      setClientes(prev => prev.map(c =>
        c.ordem_cliente === ordemCliente ? { ...c, spc: false } : c
      ));
    } catch (error) {
      console.error('Erro ao remover SPC:', error);
      alert('Erro ao remover marcação SPC');
    }
  };

  const handleCampoChange = (ordemCliente, campo, valor) => {
    console.log('✏️ Alterando campo:', { ordemCliente, campo, valor });
    
    setClientes(prevClientes => 
      prevClientes.map(c => 
        c.ordem_cliente === ordemCliente ? { ...c, [campo]: valor } : c
      )
    );
    
    setEditando(prev => ({
      ...prev,
      [ordemCliente]: true
    }));
  };

  const handleCobrancaPresencialChange = (cliente, checked) => {
    if (checked) {
      setClienteDataPresencial(cliente);
      const amanha = new Date();
      amanha.setDate(amanha.getDate() + 1);
      const dataFormatada = amanha.toISOString().split('T')[0];
      setDataAgendamentoModal(dataFormatada);
      setShowDataModal(true);
    } else {
      handleCampoChange(cliente.ordem_cliente, 'cobranca_presencial', false);
      handleCampoChange(cliente.ordem_cliente, 'data_agendamento', '');
    }
  };

  const handleConfirmarDataPresencial = () => {
    if (!dataAgendamentoModal) {
      alert('Por favor, selecione uma data para o agendamento');
      return;
    }

    setClientes(prevClientes => 
      prevClientes.map(c => 
        c.ordem_cliente === clienteDataPresencial.ordem_cliente 
          ? { 
              ...c, 
              cobranca_presencial: true,
              data_agendamento: dataAgendamentoModal 
            } 
          : c
      )
    );

    setEditando(prev => ({
      ...prev,
      [clienteDataPresencial.ordem_cliente]: true
    }));

    setShowDataModal(false);
    setClienteDataPresencial(null);
    setDataAgendamentoModal('');
  };

  const handleSalvarCliente = async (cliente) => {
    try {
      console.log('💾 Salvando cliente:', cliente.ordem_cliente);

      if (!cliente.codigo_cliente || !cliente.nome_cliente) {
        alert('Erro: dados do cliente incompletos (código ou nome ausente). Recarregue a página e tente novamente.');
        return;
      }
      
      const dados = {
        ordem_cliente: cliente.ordem_cliente,
        codigo_cliente: cliente.codigo_cliente,
        nome_cliente: cliente.nome_cliente,    
        cobranca_zap: cliente.cobranca_zap,
        cobranca_presencial: cliente.cobranca_presencial,
        resposta: cliente.resposta,
        data_agendamento: cliente.data_agendamento,
        lembrete_ativo: cliente.lembrete_ativo || false,
        data_lembrete: cliente.data_lembrete || null,
        mensagem_lembrete: cliente.mensagem_lembrete || '',
        acao: cliente.acao,
        spc: cliente.spc || false
      };

      await salvarDadosCobranca(dados);
      
      setEditando(prev => {
        const novo = { ...prev };
        delete novo[cliente.ordem_cliente];
        return novo;
      });

      alert('Dados salvos com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar dados:', error);
      alert('Erro ao salvar dados do cliente');
    }
  };

  const handleConfirmarLembrete = () => {
    if (!dataLembreteModal) {
      alert('Por favor, selecione uma data para o lembrete');
      return;
    }

    if (!mensagemLembreteModal.trim()) {
      alert('Por favor, informe a mensagem do lembrete');
      return;
    }

    setClientes(prevClientes => 
      prevClientes.map(c => 
        c.ordem_cliente === clienteLembrete.ordem_cliente 
          ? { 
              ...c, 
              lembrete_ativo: true,
              data_lembrete: dataLembreteModal,
              mensagem_lembrete: mensagemLembreteModal
            } 
          : c
      )
    );

    setEditando(prev => ({
      ...prev,
      [clienteLembrete.ordem_cliente]: true
    }));

    setShowLembreteModal(false);
    setClienteLembrete(null);
    setDataLembreteModal('');
    setMensagemLembreteModal('');
  };

  /**
   * Gerar mensagem com PARCELAS individuais (SEM VALORES)
   */
  const gerarMensagemComParcelas = (cliente) => {
    const formatarData = (data) => {
      const d = new Date(data);
      return d.toLocaleDateString('pt-BR');
    };
    
    // Se não tem parcelas detalhadas, usar mensagem padrão
    if (!cliente.parcelas || cliente.parcelas.length === 0) {
      return `🔔 *COBRANÇA AUTOMÁTICA*

Olá, *${cliente.nome_cliente}*! 👋

Identificamos que você possui débitos em aberto com ${cliente.dias_atraso} dias de atraso.

Por favor, regularize sua situação o quanto antes para evitar transtornos.

💬 *Para consultar os valores atualizados das suas parcelas, entre em contato conosco pelo WhatsApp.*

Em caso de dúvidas, estamos à disposição!

Atenciosamente,
*Equipe de Cobrança*`;
    }
    
    // Ordenar parcelas por data de vencimento
    const parcelasOrdenadas = [...cliente.parcelas].sort((a, b) => 
      new Date(a.data_vencimento) - new Date(b.data_vencimento)
    );
    
    // Montar lista de parcelas (SEM VALORES)
    let listaParcelas = '';
    parcelasOrdenadas.forEach((parcela, index) => {
      const emoji = parcela.dias_atraso > 60 ? '🔴' : parcela.dias_atraso > 30 ? '🟡' : '⚠️';
      listaParcelas += `\n${emoji} *Parcela ${index + 1}*`;
      listaParcelas += `\n   📄 Nota: ${parcela.nota || 'N/A'}`;
      listaParcelas += `\n   📅 Vencimento: ${formatarData(parcela.data_vencimento)}`;
      listaParcelas += `\n   ⏰ Atraso: ${parcela.dias_atraso} dias`;
      
      if (index < parcelasOrdenadas.length - 1) {
        listaParcelas += '\n';
      }
    });
    
    return `🔔 *COBRANÇA AUTOMÁTICA*

Olá, *${cliente.nome_cliente}*! 👋

Identificamos que você possui *${cliente.quantidade_contas} parcela${cliente.quantidade_contas > 1 ? 's' : ''}* em aberto conosco:
${listaParcelas}

━━━━━━━━━━━━━━━━━━━━

⚠️ *Para regularizar sua situação e evitar juros adicionais, entre em contato conosco o quanto antes.*

💬 *Os valores atualizados de cada parcela podem ser consultados diretamente pelo WhatsApp.*

📞 *Dúvidas?* Estamos à disposição!

Atenciosamente,
*Equipe de Cobrança*`;
  };

  /**
   * Abrir modal para visualizar parcelas
   */
  const handleVerParcelas = (cliente) => {
    setClienteParcelas(cliente);
    setShowParcelasModal(true);
  };

  const handleAbrirModalWhatsApp = (cliente) => {
    console.log('📱 Abrindo modal WhatsApp para:', cliente.nome_cliente);
    setClienteSelecionado(cliente);
    setTelefoneModal(cliente.telefone || '');
    
    // Usar a função de mensagem com parcelas
    const mensagemGerada = gerarMensagemComParcelas(cliente);
    setMensagemModal(mensagemGerada);
    
    setShowWhatsAppModal(true);
  };

  const handleConfirmarEnvio = async () => {
    if (!telefoneModal) {
      alert('Por favor, informe o telefone do cliente');
      return;
    }

    if (!mensagemModal.trim()) {
      alert('Por favor, informe a mensagem de cobrança');
      return;
    }

    try {
      setEnviando(true);
      console.log('📤 Enviando cobrança via WhatsApp:', {
        ordem_cliente: clienteSelecionado.ordem_cliente,
        telefone: telefoneModal
      });
      
      const response = await enviarCobrancaWhatsApp({
        ordem_cliente: clienteSelecionado.ordem_cliente,
        telefone: telefoneModal,
        mensagem: mensagemModal
      });
      
      console.log('✅ Resposta do envio:', response);
      
      // Marcar cobrança zap automaticamente
      if (response.sucesso || response.success) {
        console.log('✅ Mensagem enviada! Marcando cobrança_zap automaticamente...');
        
        const agora = new Date().toISOString();
        const data30Dias = new Date();
        data30Dias.setDate(data30Dias.getDate() + 30);
        const dataLembrete30 = data30Dias.toISOString().split('T')[0];

        setClientes(prevClientes =>
          prevClientes.map(c =>
            c.ordem_cliente === clienteSelecionado.ordem_cliente
              ? { ...c, cobranca_zap: true, data_ultima_cobranca_zap: agora, aviso_reenvio_zap: false }
              : c
          )
        );

        try {
          const clienteAtual = clientes.find(c => c.ordem_cliente === clienteSelecionado.ordem_cliente) || clienteSelecionado;
          await salvarDadosCobranca({
            ordem_cliente: clienteAtual.ordem_cliente,
            codigo_cliente: clienteAtual.codigo_cliente,
            nome_cliente: clienteAtual.nome_cliente,
            cobranca_zap: true,
            cobranca_presencial: clienteAtual.cobranca_presencial || false,
            resposta: clienteAtual.resposta || '',
            data_agendamento: clienteAtual.data_agendamento || '',
            lembrete_ativo: true,
            data_lembrete: dataLembrete30,
            mensagem_lembrete: `Reenviar cobrança WhatsApp - verificar débitos em atraso (envio original: ${new Date().toLocaleDateString('pt-BR')})`,
            acao: clienteAtual.acao || '',
            spc: clienteAtual.spc || false
          });
          console.log('✅ Cobrança Zap marcada, lembrete de reenvio criado para', dataLembrete30);
        } catch (saveError) {
          console.error('❌ Erro ao salvar marcação automática:', saveError);
        }
      }
      
      setResultado(response);
      setShowWhatsAppModal(false);
      setShowResultModal(true);
      
      await loadData();
    } catch (error) {
      console.error('❌ Erro ao enviar cobrança:', error);
      setResultado({
        sucesso: false,
        success: false,
        message: error.message || 'Erro ao enviar mensagem de cobrança'
      });
      setShowWhatsAppModal(false);
      setShowResultModal(true);
    } finally {
      setEnviando(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatarTelefone = (tel) => {
    if (!tel) return '';
    const numero = tel.replace(/\D/g, '');
    if (numero.length === 11) {
      return `(${numero.substr(0,2)}) ${numero.substr(2,5)}-${numero.substr(7)}`;
    }
    if (numero.length === 10) {
      return `(${numero.substr(0,2)}) ${numero.substr(2,4)}-${numero.substr(6)}`;
    }
    return tel;
  };

  const formatarData = (data) => {
    if (!data) return '';
    const d = new Date(data);
    return d.toLocaleDateString('pt-BR');
  };

  // Cálculo da paginação
  const totalPaginas = Math.ceil(clientesFiltrados.length / itensPorPagina);
  const indiceInicial = (paginaAtual - 1) * itensPorPagina;
  const clientesPaginados = clientesFiltrados.slice(indiceInicial, indiceInicial + itensPorPagina);

  const whatsappConectado = whatsappStatus?.conectado === true;

  const columns = [
    {
      header: 'Cliente',
      accessor: 'nome_cliente',
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 truncate">{row.nome_cliente}</div>
            <div className="text-sm text-gray-500">{formatarTelefone(row.telefone) || 'Sem telefone'}</div>
            {editando[row.ordem_cliente] && (
              <div className="flex items-center gap-1 mt-1">
                <AlertTriangle className="w-3 h-3 text-orange-500" />
                <span className="text-xs text-orange-600 font-medium">Não salvo</span>
              </div>
            )}
          </div>
          <div className="flex gap-1">
            {/* Botão Ver Parcelas */}
            {row.parcelas && row.parcelas.length > 0 && (
              <button
                onClick={() => handleVerParcelas(row)}
                className="flex-shrink-0 flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs whitespace-nowrap"
                title="Ver parcelas detalhadas"
              >
                <FileText className="w-3 h-3" />
                <span>{row.quantidade_contas}</span>
              </button>
            )}
            {/* Botão WhatsApp */}
            <button
              onClick={() => handleAbrirModalWhatsApp(row)}
              disabled={!whatsappConectado || !row.telefone}
              className="flex-shrink-0 flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-xs whitespace-nowrap"
              title={!whatsappConectado ? 'WhatsApp desconectado' : !row.telefone ? 'Cliente sem telefone' : 'Enviar cobrança via WhatsApp'}
            >
              <MessageCircle className="w-3 h-3" />
              <span>WhatsApp</span>
            </button>
          </div>
        </div>
      )
    },
    {
      header: (
        <button
          onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
          className="flex items-center gap-1 uppercase font-medium text-gray-500 hover:text-gray-700 transition-colors"
          title={sortOrder === 'asc' ? 'Menor atraso primeiro — clique para inverter' : 'Maior atraso primeiro — clique para inverter'}
        >
          Dias/Valor
          <span className="flex flex-col leading-none">
            <ArrowUp className={`w-3 h-3 ${sortOrder === 'asc' ? 'text-red-600' : 'text-gray-300'}`} />
            <ArrowDown className={`w-3 h-3 ${sortOrder === 'desc' ? 'text-red-600' : 'text-gray-300'}`} />
          </span>
        </button>
      ),
      render: (row) => (
        <div className="whitespace-nowrap">
          <StatusBadge
            status={row.dias_atraso >= 60 ? 'danger' : 'warning'}
            text={`${row.dias_atraso} dias`}
          />
          <div className="text-sm font-medium text-gray-900 mt-1">
            {formatCurrency(row.valor_em_aberto)}
          </div>
          {row.quantidade_contas > 1 && (
            <div className="text-xs text-gray-500 mt-1">
              {row.quantidade_contas} parcelas
            </div>
          )}
        </div>
      )
    },
    {
      header: 'Cobrança Zap',
      render: (row) => (
        <div className="flex flex-col items-center gap-1">
          <input
            type="checkbox"
            checked={row.cobranca_zap}
            disabled
            className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded cursor-not-allowed opacity-75"
            title="Marcado automaticamente após envio de mensagem"
          />
          {row.data_ultima_cobranca_zap && (
            <span className="text-xs text-gray-500" title="Data do último envio">
              {formatarData(row.data_ultima_cobranca_zap)}
            </span>
          )}
          {row.aviso_reenvio_zap && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full border border-orange-300 animate-pulse"
              title="30 dias desde o último envio — cliente ainda possui débitos em atraso"
            >
              ⚠ Reenviar
            </span>
          )}
        </div>
      )
    },
    {
      header: 'Cobrança Presencial',
      render: (row) => (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            checked={row.cobranca_presencial}
            onChange={(e) => handleCobrancaPresencialChange(row, e.target.checked)}
            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
            title="Marcar para agendar cobrança presencial"
          />
        </div>
      )
    },
    {
      header: 'Resposta',
      render: (row) => (
        <select
          value={row.resposta}
          onChange={(e) => handleCampoChange(row.ordem_cliente, 'resposta', e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Selecione...</option>
          <option value="pagara_semana">Pagará essa semana</option>
          <option value="pagara_mes">Pagará esse mês</option>
          <option value="sem_previsao">Não pagará ou sem previsão</option>
          <option value="sem_contato">Não conseguido contato</option>
        </select>
      )
    },
    {
      header: 'Agendar Lembrete',
      render: (row) => (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={row.lembrete_ativo || false}
            onChange={(e) => {
              const checked = e.target.checked;
              if (checked) {
                setClienteLembrete(row);
                const amanha = new Date();
                amanha.setDate(amanha.getDate() + 1);
                const dataFormatada = amanha.toISOString().split('T')[0];
                setDataLembreteModal(dataFormatada);
                setMensagemLembreteModal(`Lembrete: Cliente ${row.nome_cliente} com débito de ${formatCurrency(row.valor_em_aberto)}`);
                setShowLembreteModal(true);
              } else {
                handleCampoChange(row.ordem_cliente, 'lembrete_ativo', false);
                handleCampoChange(row.ordem_cliente, 'data_lembrete', '');
                handleCampoChange(row.ordem_cliente, 'mensagem_lembrete', '');
                // Se tinha ação de lembrete 30 dias, limpar também
                if (row.acao === 'lembrete_30_dias') {
                  handleCampoChange(row.ordem_cliente, 'acao', '');
                }
              }
            }}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
            title={row.acao === 'lembrete_30_dias' ? 'Lembrete configurado automaticamente (30 dias)' : 'Agendar lembrete manual'}
          />
          {row.lembrete_ativo && row.data_lembrete && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500">
                {formatarData(row.data_lembrete)}
              </span>
              {row.acao === 'lembrete_30_dias' && (
                <span className="text-xs text-blue-600 font-medium" title="Configurado automaticamente">
                  (Auto)
                </span>
              )}
            </div>
          )}
        </div>
      )
    },
    {
      header: 'Ação',
      render: (row) => (
        <select
          value={row.acao}
          onChange={(e) => {
            const novaAcao = e.target.value;
            handleCampoChange(row.ordem_cliente, 'acao', novaAcao);
            
            // ⭐ Se selecionar "lembrete_30_dias", configurar lembrete automaticamente
            if (novaAcao === 'lembrete_30_dias') {
              const data30Dias = new Date();
              data30Dias.setDate(data30Dias.getDate() + 30);
              const dataFormatada = data30Dias.toISOString().split('T')[0];
              
              // Marcar lembrete ativo e definir data
              handleCampoChange(row.ordem_cliente, 'lembrete_ativo', true);
              handleCampoChange(row.ordem_cliente, 'data_lembrete', dataFormatada);
              handleCampoChange(row.ordem_cliente, 'mensagem_lembrete', 
                `Lembrete automático: Cliente ${row.nome_cliente} com débito de ${formatCurrency(row.valor_em_aberto)} - Acompanhar cobrança`
              );
              
              console.log(`✅ Lembrete automático configurado para ${dataFormatada}`);
            }
            // Se mudar para outra opção, desmarcar lembrete
            else if (row.acao === 'lembrete_30_dias' && novaAcao !== 'lembrete_30_dias') {
              handleCampoChange(row.ordem_cliente, 'lembrete_ativo', false);
              handleCampoChange(row.ordem_cliente, 'data_lembrete', '');
              handleCampoChange(row.ordem_cliente, 'mensagem_lembrete', '');
            }
          }}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Selecione...</option>
          <option value="lembrete_30_dias">Lembrete após 30 dias</option>
          <option value="cobranca_presencial">Enviar cobrança presencial</option>
          <option value="negativar">Negativar cliente</option>
        </select>
      )
    },
    {
      header: 'SPC',
      render: (row) => (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            checked={row.spc || false}
            onChange={(e) => handleCampoChange(row.ordem_cliente, 'spc', e.target.checked)}
            className="w-5 h-5 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2 cursor-pointer"
            title="Marcar se cliente está no SPC"
          />
        </div>
      )
    },
    {
      header: 'Salvar',
      render: (row) => (
        <div className="flex items-center justify-center">
          {editando[row.ordem_cliente] && (
            <button
              onClick={() => handleSalvarCliente(row)}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
              title="Salvar alterações"
            >
              <Save className="w-4 h-4" />
              <span>Salvar</span>
            </button>
          )}
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Carregando clientes..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cobrança via WhatsApp</h1>
          <p className="text-gray-600 mt-1">Gerenciamento de cobranças e acompanhamento de clientes</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <Phone className="w-5 h-5 text-gray-600" />
              <span className="text-sm text-gray-600 font-medium">WhatsApp:</span>
              {whatsappConectado ? (
                <StatusBadge status="success" text="Conectado" />
              ) : (
                <StatusBadge status="danger" text="Desconectado" />
              )}
            </div>
            <button
              onClick={handleConsultarSpc}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
              <AlertTriangle className="w-4 h-4" />
              Clientes no SPC
            </button>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          </div>
          <button
            onClick={() => {
              setShowZapModal(true);
              handleConsultarCobrancasZap(dataInicioZap, dataFimZap);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            Listar Cobranças por Zap
          </button>
        </div>
      </div>

      {/* Aviso de Alterações Não Salvas */}
      {totalAlteracoesNaoSalvas > 0 && (
        <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-orange-900">Alterações Não Salvas</p>
              <p className="text-sm text-orange-800 mt-1">
                Você possui <strong>{totalAlteracoesNaoSalvas}</strong> cliente{totalAlteracoesNaoSalvas > 1 ? 's' : ''} com alterações não salvas. 
                Clique no botão <strong>"Salvar"</strong> na última coluna de cada cliente para salvar as alterações.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Busca por Cliente e Filtros */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <Search className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Busca e Filtros</h2>
        </div>
        
        {/* Campo de Busca */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Buscar por Nome ou Código do Cliente
          </label>
          <input
            type="text"
            value={buscaCliente}
            onChange={(e) => setBuscaCliente(e.target.value)}
            placeholder="Digite o nome ou código do cliente..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            A busca é aplicada automaticamente conforme você digita
          </p>
        </div>

        {/* Filtros de Período */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dias mínimos de atraso
            </label>
            <input
              type="number"
              value={diasMin}
              onChange={(e) => setDiasMin(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              min="0"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dias máximos de atraso
            </label>
            <input
              type="number"
              value={diasMax}
              onChange={(e) => setDiasMax(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              min="0"
            />
          </div>
          
          <div className="flex items-end">
            <button
              onClick={aplicarFiltro}
              className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Search className="w-4 h-4" />
              Pesquisar
            </button>
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Filtro ativo:</strong> Mostrando clientes com {diasMin} a {diasMax} dias de atraso
            {diasMin === 30 && diasMax === 360 && " (padrão)"}
            {buscaCliente.trim() && ` • Busca: "${buscaCliente}"`}
          </p>
        </div>
      </div>

      {/* Alerta se WhatsApp desconectado */}
      {!whatsappConectado && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Phone className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-900">WhatsApp Desconectado</p>
              <p className="text-sm text-yellow-700 mt-1">
                Conecte o WhatsApp nas configurações para enviar mensagens de cobrança.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info sobre Cobrança Automática */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <MessageCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900">Instruções de Uso</p>
            <p className="text-sm text-blue-700 mt-1">
              • <strong>Alterações nos campos:</strong> Clique no botão "Salvar" para gravar as mudanças<br />
              • <strong>Cobrança Zap:</strong> Marcada automaticamente ao enviar mensagem via WhatsApp<br />
              • <strong>Mensagem de Cobrança:</strong> Parcelas listadas sem valores - cliente consulta valores pelo WhatsApp<br />
              • <strong>Cobrança Presencial:</strong> Ao marcar, será solicitada a data do agendamento<br />
              • <strong>Ação - Lembrete após 30 dias:</strong> Configura automaticamente um lembrete para daqui a 30 dias<br />
              • <strong>Agendar Lembrete:</strong> Permite configurar lembrete manual em data personalizada<br />
              • <strong>Reenviar (badge laranja):</strong> Indica 30+ dias desde o último envio — cliente ainda possui débitos. Um lembrete é criado automaticamente na tela Lembretes para a data de 30 dias após o envio<br />
              • <strong>SPC:</strong> Marque se o cliente está registrado no SPC
            </p>
          </div>
        </div>
      </div>

      {/* Aviso de Reenvio Pendente */}
      {clientesFiltrados.filter(c => c.aviso_reenvio_zap).length > 0 && (
        <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-orange-900">Reenvio WhatsApp Pendente</p>
              <p className="text-sm text-orange-800 mt-1">
                <strong>{clientesFiltrados.filter(c => c.aviso_reenvio_zap).length}</strong> cliente{clientesFiltrados.filter(c => c.aviso_reenvio_zap).length > 1 ? 's' : ''} com mais de 30 dias desde o último envio e ainda possuem débitos em atraso. Verifique a coluna <strong>"Cobrança Zap"</strong> para identificá-los.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600">Total de Clientes</p>
          <p className="text-2xl font-bold text-gray-900">{clientesFiltrados.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600">Valor Total em Aberto</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(clientesFiltrados.reduce((sum, c) => sum + c.valor_em_aberto, 0))}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600">Com Telefone</p>
          <p className="text-2xl font-bold text-gray-900">
            {clientesFiltrados.filter(c => c.telefone).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600">Reenvio Pendente</p>
          <p className="text-2xl font-bold text-orange-600">
            {clientesFiltrados.filter(c => c.aviso_reenvio_zap).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600">Alterações Não Salvas</p>
          <p className="text-2xl font-bold text-orange-600">
            {totalAlteracoesNaoSalvas}
          </p>
        </div>
      </div>

      {/* Tabela de Clientes */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <Table
            columns={columns}
            data={clientesPaginados}
            emptyMessage="Nenhum cliente encontrado com os critérios informados"
          />
        </div>
        
        {/* Paginação */}
        {totalPaginas > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Mostrando {indiceInicial + 1} a {Math.min(indiceInicial + itensPorPagina, clientesFiltrados.length)} de {clientesFiltrados.length} clientes
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPaginaAtual(prev => Math.max(1, prev - 1))}
                disabled={paginaAtual === 1}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <span className="px-4 py-2 text-sm font-medium text-gray-700">
                Página {paginaAtual} de {totalPaginas}
              </span>
              <button
                onClick={() => setPaginaAtual(prev => Math.min(totalPaginas, prev + 1))}
                disabled={paginaAtual === totalPaginas}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Visualização de Parcelas */}
      <Modal
        isOpen={showParcelasModal}
        onClose={() => {
          setShowParcelasModal(false);
          setClienteParcelas(null);
        }}
        title="Parcelas em Aberto"
      >
        {clienteParcelas && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Cliente</p>
              <p className="font-medium text-gray-900 text-lg">{clienteParcelas.nome_cliente}</p>
              <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total em Aberto: </span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(clienteParcelas.valor_em_aberto)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Quantidade: </span>
                  <span className="font-semibold text-gray-900">
                    {clienteParcelas.quantidade_contas} parcela{clienteParcelas.quantidade_contas > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {clienteParcelas.parcelas && clienteParcelas.parcelas.sort((a, b) => 
                new Date(a.data_vencimento) - new Date(b.data_vencimento)
              ).map((parcela, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-2 ${
                    parcela.dias_atraso > 60 
                      ? 'bg-red-50 border-red-200' 
                      : parcela.dias_atraso > 30 
                      ? 'bg-yellow-50 border-yellow-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">
                      Parcela {index + 1}
                    </span>
                    <StatusBadge
                      status={parcela.dias_atraso > 60 ? 'danger' : parcela.dias_atraso > 30 ? 'warning' : 'default'}
                      text={`${parcela.dias_atraso} dias`}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Nota: </span>
                      <span className="font-medium text-gray-900">{parcela.nota || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Vencimento: </span>
                      <span className="font-medium text-gray-900">{formatarData(parcela.data_vencimento)}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-600">Valor: </span>
                      <span className="font-bold text-gray-900 text-lg">{formatCurrency(parcela.valor_aberto)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={() => {
                  setShowParcelasModal(false);
                  setClienteParcelas(null);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Data para Cobrança Presencial */}
      <Modal
        isOpen={showDataModal}
        onClose={() => {
          setShowDataModal(false);
          setClienteDataPresencial(null);
        }}
        title="Agendar Cobrança Presencial"
      >
        {clienteDataPresencial && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Agendamento de Visita</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Selecione a data para visitar o cliente e realizar a cobrança presencial.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Cliente</p>
              <p className="font-medium text-gray-900 text-lg">{clienteDataPresencial.nome_cliente}</p>
              <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Valor: </span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(clienteDataPresencial.valor_em_aberto)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Atraso: </span>
                  <span className="font-semibold text-gray-900">
                    {clienteDataPresencial.dias_atraso} dias
                  </span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data do Agendamento *
              </label>
              <input
                type="date"
                value={dataAgendamentoModal}
                onChange={(e) => setDataAgendamentoModal(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-gray-500 mt-1">
                Selecione quando você planeja visitar este cliente
              </p>
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t">
              <button
                onClick={() => {
                  setShowDataModal(false);
                  setClienteDataPresencial(null);
                }}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <X className="w-4 h-4" />
                Cancelar
              </button>
              <button
                onClick={handleConfirmarDataPresencial}
                disabled={!dataAgendamentoModal}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                <Check className="w-4 h-4" />
                Confirmar Agendamento
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Confirmação WhatsApp */}
      <Modal
        isOpen={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        title="Confirmar Envio de Cobrança"
      >
        {clienteSelecionado && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Cliente</p>
              <p className="font-medium text-gray-900">{clienteSelecionado.nome_cliente}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Valor em Aberto</p>
                <p className="font-medium text-gray-900">{formatCurrency(clienteSelecionado.valor_em_aberto)}</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Dias de Atraso</p>
                <p className="font-medium text-gray-900">{clienteSelecionado.dias_atraso} dias</p>
              </div>
            </div>

            {clienteSelecionado.parcelas && clienteSelecionado.parcelas.length > 1 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Múltiplas Parcelas:</strong> Este cliente possui {clienteSelecionado.quantidade_contas} parcelas em aberto. A mensagem incluirá apenas vencimento e dias de atraso (sem valores).
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Número de Telefone
              </label>
              <input
                type="tel"
                value={telefoneModal}
                onChange={(e) => setTelefoneModal(e.target.value)}
                placeholder="(00) 00000-0000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Verifique se o telefone está correto antes de enviar
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem de Cobrança
              </label>
              <textarea
                value={mensagemModal}
                onChange={(e) => setMensagemModal(e.target.value)}
                rows={clienteSelecionado.parcelas && clienteSelecionado.parcelas.length > 1 ? 12 : 8}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="Digite a mensagem de cobrança..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Você pode editar a mensagem antes de enviar
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>💬 Atenção:</strong> A mensagem não inclui valores das parcelas. Cliente deve consultar valores atualizados pelo WhatsApp.
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                <strong>✅ Marcação Automática:</strong> Ao enviar esta mensagem, a "Cobrança Zap" será marcada automaticamente para este cliente.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowWhatsAppModal(false)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <X className="w-4 h-4" />
                Cancelar
              </button>
              <button
                onClick={handleConfirmarEnvio}
                disabled={enviando || !telefoneModal || !mensagemModal.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                <Check className="w-4 h-4" />
                {enviando ? 'Enviando...' : 'Confirmar Envio'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Resultado */}
      <Modal
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        title="Resultado do Envio"
      >
        {resultado && (
          <div className="space-y-4">
            {(resultado.sucesso || resultado.success) ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-900">Mensagem enviada com sucesso!</p>
                    <p className="text-sm text-green-700 mt-1">
                      A cobrança foi enviada via WhatsApp e a marcação "Cobrança Zap" foi ativada automaticamente.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-900">Erro ao enviar mensagem</p>
                    <p className="text-sm text-red-700 mt-1">{resultado.message || 'Erro desconhecido'}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowResultModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Lembrete */}
      <Modal
        isOpen={showLembreteModal}
        onClose={() => {
          setShowLembreteModal(false);
          setClienteLembrete(null);
          setDataLembreteModal('');
          setMensagemLembreteModal('');
        }}
        title="Agendar Lembrete"
      >
        {clienteLembrete && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Configurar Lembrete</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Defina a data e mensagem para o lembrete deste cliente.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Cliente</p>
              <p className="font-medium text-gray-900 text-lg">{clienteLembrete.nome_cliente}</p>
              <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Valor: </span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(clienteLembrete.valor_em_aberto)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Atraso: </span>
                  <span className="font-semibold text-gray-900">
                    {clienteLembrete.dias_atraso} dias
                  </span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data do Lembrete *
              </label>
              <input
                type="date"
                value={dataLembreteModal}
                onChange={(e) => setDataLembreteModal(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-gray-500 mt-1">
                Selecione quando você deseja receber este lembrete
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem do Lembrete *
              </label>
              <textarea
                value={mensagemLembreteModal}
                onChange={(e) => setMensagemLembreteModal(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Digite a mensagem do lembrete..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Esta mensagem será exibida no lembrete
              </p>
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t">
              <button
                onClick={() => {
                  setShowLembreteModal(false);
                  setClienteLembrete(null);
                  setDataLembreteModal('');
                  setMensagemLembreteModal('');
                }}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <X className="w-4 h-4" />
                Cancelar
              </button>
              <button
                onClick={handleConfirmarLembrete}
                disabled={!dataLembreteModal || !mensagemLembreteModal.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                <Check className="w-4 h-4" />
                Confirmar Lembrete
              </button>
            </div>
          </div>
        )}
      </Modal>
      
      {/* Modal Cobranças por Zap */}
      <Modal
        isOpen={showZapModal}
        onClose={() => setShowZapModal(false)}
        title="Cobranças Enviadas por WhatsApp"
      >
        <div className="space-y-4">
          {/* Filtro de período */}
          <div className="flex items-end gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Data início</label>
              <input
                type="date"
                value={dataInicioZap}
                onChange={e => setDataInicioZap(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Data fim</label>
              <input
                type="date"
                value={dataFimZap}
                onChange={e => setDataFimZap(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
            <button
              onClick={() => handleConsultarCobrancasZap(dataInicioZap, dataFimZap)}
              disabled={loadingZap}
              className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors text-sm"
            >
              <Search className="w-4 h-4" />
              Filtrar
            </button>
          </div>

          {loadingZap ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="md" text="Buscando cobranças..." />
            </div>
          ) : cobrancasZap.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhuma cobrança enviada por WhatsApp no período selecionado.
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                <strong>{cobrancasZap.length}</strong> cliente{cobrancasZap.length !== 1 ? 's' : ''} cobrado{cobrancasZap.length !== 1 ? 's' : ''} por WhatsApp no período
              </p>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="sticky top-0">
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-3 py-2 font-medium text-gray-700">#</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Cliente</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Cód.</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Último Envio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cobrancasZap.map((item, idx) => (
                      <tr key={item.ordem_cliente} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-400 text-xs">{idx + 1}</td>
                        <td className="px-3 py-2 font-medium text-gray-900">
                          {item.nome_cliente || `Cliente ${item.ordem_cliente}`}
                        </td>
                        <td className="px-3 py-2 text-gray-600">{item.codigo_cliente}</td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {new Date(item.data_ultima_cobranca_zap).toLocaleString('pt-BR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end pt-2 border-t">
                <button
                  onClick={() => setShowZapModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Modal SPC */}
      <Modal
        isOpen={showSpcModal}
        onClose={() => setShowSpcModal(false)}
        title="Clientes com SPC"
      >
        <div className="space-y-4">
          {loadingSpc ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="md" text="Buscando clientes no SPC..." />
            </div>
          ) : clientesSpc.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhum cliente marcado com SPC.
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                <strong>{clientesSpc.length}</strong> cliente{clientesSpc.length > 1 ? 's' : ''} com SPC ativo
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Data Entrada</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Nome</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-700">Valor Parcela</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Vencimento</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientesSpc.map((cliente) =>
                      cliente.parcelas && cliente.parcelas.length > 0
                        ? cliente.parcelas.map((parcela, idx) => (
                            <tr key={`${cliente.ordem_cliente}-${idx}`} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                                {idx === 0 ? formatarData(cliente.created_at) : ''}
                              </td>
                              <td className="px-3 py-2 font-medium text-gray-900">
                                {idx === 0 ? cliente.nome_cliente : ''}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-900 whitespace-nowrap">
                                {formatCurrency(parcela.valor)}
                              </td>
                              <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                                {formatarData(parcela.data_vencimento)}
                                <span className="ml-1 text-xs text-red-500">
                                  ({parcela.dias_atraso}d)
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                {idx === 0 && (
                                  <button
                                    onClick={() => handleRemoverSpc(cliente.ordem_cliente)}
                                    className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors text-xs whitespace-nowrap"
                                    title="Remover SPC"
                                  >
                                    <X className="w-3 h-3" />
                                    Remover SPC
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))
                        : (
                          <tr key={cliente.ordem_cliente} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-600">{formatarData(cliente.created_at)}</td>
                            <td className="px-3 py-2 font-medium text-gray-900">{cliente.nome_cliente}</td>
                            <td className="px-3 py-2 text-center text-gray-400 italic" colSpan={2}>
                              Sem parcelas em atraso
                            </td>
                            <td className="px-3 py-2">
                              <button
                                onClick={() => handleRemoverSpc(cliente.ordem_cliente)}
                                className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors text-xs"
                                title="Remover SPC"
                              >
                                <X className="w-3 h-3" />
                                Remover SPC
                              </button>
                            </td>
                          </tr>
                        )
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end pt-2 border-t">
                <button
                  onClick={() => setShowSpcModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}